import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React from 'react';
import { debounce } from "../../../utils/helpers";
import { trapFocus } from "../../../utils/_private/a11y";
import * as keys from "../../../constants/keys";
import * as timeouts from "../../../constants/timeouts";
import { useIsDismissible } from "../../../hooks/_private/useIsDismissible";
import { useElementId } from "../../../hooks/useElementId";
import { useIsomorphicLayoutEffect } from "../../../hooks/useIsomorphicLayoutEffect";
import { useFlyout } from "../../../hooks/_private/useFlyout";
import { useKeyboardCallback } from "../../../hooks/_private/useKeyboardCallback";
import { useOnClickOutside } from "../../../hooks/_private/useOnClickOutside";
import { checkTransitions } from "../../../utils/_private/animation";
import { Provider } from './Flyout.context';
const FlyoutRoot = (props, ref) => {
    const { triggerType = 'click', returnTriggerFocus = true, focusFirstTrapElement = true, onOpen, onClose, children, forcePosition, closeOnResize = false, trapFocusMode, width, offset, contentGap, contentClassName, contentAttributes, position: passedPosition, active: passedActive, id: passedId, closeOnScroll, } = props;
    const triggerElRef = React.useRef(null);
    const flyoutElRef = React.useRef(null);
    const id = useElementId(passedId);
    const timerRef = React.useRef();
    const releaseFocusRef = React.useRef(null);
    const lockedRef = React.useRef(false);
    const shouldReturnFocusRef = React.useRef(true);
    const flyout = useFlyout(triggerElRef, flyoutElRef, {
        width,
        position: passedPosition,
        defaultActive: passedActive,
        forcePosition,
        offset,
    });
    const { status, updatePosition, render, hide, remove } = flyout;
    // Don't create dismissible queue for hover flyout because they close all together on mouseout
    const isDismissible = useIsDismissible(status !== 'idle', flyoutElRef, triggerElRef);
    const clearTimer = React.useCallback(() => {
        if (timerRef.current)
            clearTimeout(timerRef.current);
    }, [timerRef]);
    /**
     * Component open/close handlers
     * Called from the internal actions
     */
    const handleOpen = React.useCallback(() => {
        if (lockedRef.current)
            return;
        if (status === 'idle')
            onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        closeOnScroll && (document === null || document === void 0 ? void 0 : document.addEventListener('scroll', handleClose, true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, closeOnScroll]);
    const handleClose = React.useCallback(() => {
        const canClose = triggerType !== 'click' || isDismissible();
        if (!canClose)
            return;
        if (status !== 'idle')
            onClose === null || onClose === void 0 ? void 0 : onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, isDismissible, triggerType, closeOnScroll]);
    /**
     * Trigger event handlers
     */
    const handleBlur = React.useCallback((e) => {
        var _a;
        // Empty flyouts don't move the focus so they have to be closed on blur
        const focusedContent = (_a = flyoutElRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget);
        if (focusedContent)
            return;
        handleClose();
    }, [handleClose]);
    const handleFocus = React.useCallback(() => {
        if (triggerType !== 'hover')
            return;
        handleOpen();
    }, [handleOpen, triggerType]);
    const handleMouseEnter = React.useCallback(() => {
        if (triggerType !== 'hover')
            return;
        clearTimer();
        timerRef.current = setTimeout(handleOpen, timeouts.mouseEnter);
    }, [clearTimer, timerRef, handleOpen, triggerType]);
    const handleMouseLeave = React.useCallback(() => {
        if (triggerType !== 'hover')
            return;
        clearTimer();
        timerRef.current = setTimeout(handleClose, timeouts.mouseLeave);
    }, [clearTimer, timerRef, handleClose, triggerType]);
    const handleTriggerClick = React.useCallback(() => {
        if (status !== 'idle') {
            handleClose();
            return;
        }
        handleOpen();
    }, [status, handleOpen, handleClose]);
    /**
     * Control the display based on the props
     */
    useIsomorphicLayoutEffect(() => {
        if (passedActive) {
            render();
            return;
        }
        if (checkTransitions()) {
            hide();
        }
        else {
            remove();
        }
    }, [passedActive, render, hide]);
    const handleTransitionEnd = React.useCallback(() => {
        if (status === 'hidden')
            remove();
    }, [remove, status]);
    /**
     * Handle focus trap
     *
     * We release focus on visible change to not wait till animation ends
     * so if we click outside the flyout, it won't focus the trigger
     * after the animation and open it again
     *
     * Trap focus on visible to avoid scroll jumping, position is defined only when flyout is visible
     */
    useIsomorphicLayoutEffect(() => {
        if (status !== 'visible' || !flyoutElRef.current)
            return;
        releaseFocusRef.current = trapFocus(flyoutElRef.current, {
            mode: trapFocusMode,
            focusFirstTrapElement,
            // TODO: Turn includeTrigger on for input text and textarea
            includeTrigger: triggerType === 'hover',
            onNavigateOutside: () => {
                releaseFocusRef.current = null;
                handleClose();
            },
        });
    }, [status, triggerType, handleClose, trapFocusMode]);
    React.useEffect(() => {
        if (status !== 'hidden')
            return;
        if (releaseFocusRef.current) {
            /* Locking the popover to not open it again on trigger focus */
            if (triggerType === 'hover') {
                lockedRef.current = true;
                setTimeout(() => {
                    lockedRef.current = false;
                }, 100);
            }
            if (returnTriggerFocus) {
                releaseFocusRef.current({
                    withoutFocusReturn: !shouldReturnFocusRef.current,
                });
            }
            releaseFocusRef.current = null;
            shouldReturnFocusRef.current = true;
        }
    }, [status, triggerType, returnTriggerFocus]);
    /**
     * Release focus trapping on unmount
     */
    React.useEffect(() => {
        return () => {
            if (releaseFocusRef.current)
                releaseFocusRef.current();
            releaseFocusRef.current = null;
        };
    }, []);
    /**
     * Update position on resize if not closing on resize
     */
    React.useEffect(() => {
        const update = debounce(updatePosition, 10);
        !closeOnResize && window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [updatePosition, closeOnResize]);
    /**
     * Close on resize
     */
    React.useEffect(() => {
        closeOnResize && window.addEventListener('resize', handleClose);
        return () => window.removeEventListener('resize', handleClose);
    }, [handleClose, closeOnResize]);
    /**
     * Imperative methods for controlling Flyout
     */
    React.useImperativeHandle(ref, () => ({
        open: handleOpen,
        close: handleClose,
    }), [handleOpen, handleClose]);
    useKeyboardCallback(keys.ESC, () => {
        handleClose();
    }, [handleClose]);
    useOnClickOutside([flyoutElRef, triggerElRef], () => {
        // Clicking outside changes focused element so we don't need to set it back ourselves
        shouldReturnFocusRef.current = false;
        handleClose();
    });
    return (_jsx(Provider, { value: {
            id,
            flyout,
            triggerElRef,
            flyoutElRef,
            handleClose,
            handleFocus,
            handleBlur,
            handleMouseEnter,
            handleMouseLeave,
            handleTransitionEnd,
            handleClick: handleTriggerClick,
            triggerType,
            trapFocusMode,
            contentGap,
            contentClassName,
            contentAttributes,
        }, children: children }));
};
export const FlyoutControlled = React.forwardRef(FlyoutRoot);
